import { useEffect, useId, useState } from 'react'
import type { LocalModel, LocalModelAction, LocalModels as Models } from '../api/types'
import { errorMessage } from '../api/client'
import { useSessionsEnv } from '../sessions/context'

/** How often a row that is being read or downloaded asks how far it is. */
export const LOCAL_MODELS_POLL_MS = 1000
const GIB = 2 ** 30

/** Today's words (SUN-1462), said of the runner's machine rather than a Mac. */
const words = {
  title: 'Local models',
  /** Whether it fits the machine, with what it needs and what the machine has, in GB. */
  fit: {
    fits: (need: number, here: number) => `Recommended for this machine: it needs ${need} GB of memory and this machine has ${here} GB.`,
    tight: (need: number, here: number) => `Tight on this machine: it needs ${need} GB of memory and this machine has ${here} GB. Other apps will be squeezed, and it may be slow or stop.`,
    too_big: (need: number, here: number) => `Not available on this machine: it needs ${need} GB of memory and this machine has ${here} GB.`,
  },
  kind: {
    exact_edit: 'exact edits', test_writing: 'test writing', cross_file_search: 'file searches',
    diff_review: 'diff reviews', summary: 'summaries', board_update_draft: 'board drafts',
  },
  screened: (size: string, kinds: string, cases: number, attempts: number) =>
    `${size} on disk · Synthetic screening: ${kinds} accepted · ${cases} cases per kind, ${attempts} runs each`,
  absent: 'Not on this machine',
  left: (size: string) => `${size} left to download`,
  checking: 'Checking a file found on this machine',
  unchecked: 'A file on this machine was not checked',
  ready: 'Ready',
  downloading: 'Downloading',
  checkingDownload: 'Checking the download',
  progress: (done: string, total: string) => `${done} of ${total}`,
  download: 'Download',
  cancel: 'Cancel',
  check: 'Check it',
  problem: {
    no_space: 'There is not enough free space on this disk.',
    unreachable: 'The download stopped. Download again to go on.',
    refused: 'The file could not be downloaded.',
    wrong_file: 'The download was not the file Poulpe tested. It was removed.',
    failed: 'That did not work. Try again.',
  },
} as const

/** A file's size as the Finder says it: `22 GB`, `8.6 GB`, `12 MB`. */
export function fileSize(bytes: number): string {
  if (bytes < 999_500_000) return `${Math.max(bytes > 0 ? 1 : 0, Math.round(bytes / 1e6))} MB`
  return `${(bytes / 1e9).toFixed(1).replace(/\.0$/, '')} GB`
}

/** Memory in the gigabytes a machine is sold with. What a model needs is rounded up: 24.3 needs 25. */
const memoryNeeded = (bytes: number) => Math.ceil(bytes / GIB)
const memoryHere = (bytes: number) => Math.round(bytes / GIB)

const under = (row: LocalModel) => row.state === 'checking' || row.state === 'downloading'

/** Where the model stands, in one line. */
function stateOf(row: LocalModel): string {
  const far = row.progress && row.progress.total > 0 ? words.progress(fileSize(row.progress.done), fileSize(row.progress.total)) : null
  switch (row.state) {
    case 'downloading': return row.phase === 'checking' ? words.checkingDownload : far ? `${words.downloading} · ${far}` : words.downloading
    case 'checking': return far ? `${words.checking} · ${far}` : words.checking
    case 'unchecked': return words.unchecked
    case 'ready': return words.ready
    // Said with what is left only when some of it is here already.
    case 'absent': return row.download_bytes > 0 && row.download_bytes < row.bytes ? `${words.absent} · ${words.left(fileSize(row.download_bytes))}` : words.absent
  }
}

/** The one thing to do with a row, as it stands. A model too big for the machine can only be cancelled. */
function actionOf(row: LocalModel): [LocalModelAction['kind'], string] | null {
  if (under(row)) return ['cancel', words.cancel]
  if (row.fit === 'too_big' || row.state === 'ready') return null
  if (row.state === 'unchecked') return ['check', words.check]
  return ['download', words.download]
}

/**
 * The local models of the runner's machine (Poulpe-447), drawn as today's
 * Settings draws them: one row for each model Poulpe has tested, with whether
 * it fits the machine, what was measured, where it stands and one button.
 * Nothing downloads but on that button; while a model is read or downloaded,
 * the rows ask how far it is. Nothing here names a file, a folder or a hash:
 * the runner downloads and checks them (crates/poulpe-runner/src/local_models.rs).
 */
export function LocalModels({ runner, unavailable }: { runner: string; unavailable: boolean }) {
  const { api } = useSessionsEnv()
  const title = useId()
  const [answer, setAnswer] = useState<Models | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let active = true
    // Each read replaces the last: nothing older is shown as current.
    setAnswer(null)
    setProblem(null)
    if (unavailable || !api.localModels) return
    api.localModels(runner).then(read => { if (active) setAnswer(read) }, error => { if (active) setProblem(errorMessage(error)) })
    return () => { active = false }
  }, [runner, unavailable, api])

  const underWay = answer?.models.some(under) ?? false
  useEffect(() => {
    const read = api.localModels
    if (!underWay || unavailable || !read) return
    let active = true
    let timer = 0
    const ask = () => {
      timer = window.setTimeout(() => {
        // Once nothing is under way the effect ends, and takes the next asking with it.
        read(runner).then(next => { if (active) { setAnswer(next); ask() } }, () => { if (active) ask() })
      }, LOCAL_MODELS_POLL_MS)
    }
    ask()
    return () => { active = false; window.clearTimeout(timer) }
  }, [underWay, runner, unavailable, api])

  async function act(kind: LocalModelAction['kind'], row: LocalModel) {
    if (!api.localModel) return
    setBusy(true)
    setProblem(null)
    try {
      setAnswer(await api.localModel(runner, { kind, model: row.id }))
    } catch (error) {
      setProblem(errorMessage(error))
      // What the runner refused may be about a file that changed: the rows are read again.
      await api.localModels?.(runner).then(setAnswer, () => {})
    } finally {
      setBusy(false)
    }
  }

  if (!answer) return problem ? <p role="alert" className="text-danger">{problem}</p> : null
  if (!answer.models.length) return null
  const working = answer.models.some(under)
  return <section className="settings-group" aria-labelledby={title}>
    <h3 id={title}>{words.title}</h3>
    <ul className="settings-rows">
      {answer.models.map(row => {
        const action = actionOf(row)
        const state = row.fit === 'too_big' && !under(row) ? null : stateOf(row)
        const said = row.problem ? words.problem[row.problem] : null
        const far = under(row) && row.phase !== 'checking' && row.progress && row.progress.total > 0 ? row.progress : null
        const kinds = row.measured.by_kind.map(item => `${words.kind[item.kind]} ${item.accepted[0]}/${item.accepted[1]}`).join(', ')
        return <li key={row.id}>
          <div role="group" aria-label={row.name}>
            <div className="settings-row-line settings-model-line">
              <span className="settings-row-what">
                <strong>{row.name}</strong>
                <span className={row.fit === 'tight' ? 'settings-model-tight' : 'muted'}>{words.fit[row.fit](memoryNeeded(row.memory_bytes), memoryHere(answer.memory_bytes))}</span>
                <span className="muted">{words.screened(fileSize(row.bytes), kinds, row.measured.cases_per_kind, row.measured.attempts_per_case)}</span>
                {state && <span className="muted">{state}</span>}
                {said && <span className="text-danger">{said}</span>}
              </span>
              {action && <button type="button" className={action[0] === 'cancel' ? 'button button-quiet' : 'button'}
                // One thing at a time on the machine: while a model is read or downloaded, only that can be cancelled.
                disabled={busy || unavailable || (working && !under(row))} onClick={() => void act(action[0], row)}>{action[1]}</button>}
            </div>
            {far && <progress className="settings-model-progress" value={far.done} max={far.total} aria-label={`${row.name}: ${state}`} />}
          </div>
        </li>
      })}
    </ul>
    {problem && <p role="alert" className="text-danger">{problem}</p>}
  </section>
}
